<template>
  <AlertProvider>
    <AlertBridgeSetup />
    <LoadingOverlay />
    <component :is="layoutComp">
      <router-view v-slot="{ Component }">
        <KeepAlive>
          <component :is="Component" v-if="$route.meta.keepAlive" :key="$route.name" />
        </KeepAlive>
        <component :is="Component" v-if="!$route.meta.keepAlive" :key="$route.path" />
      </router-view>
    </component>
    <!-- 팝업매니저는 레이아웃 밖에서 전체 페이지 기준으로 렌더링 -->
    <PopupManager :popups="popups" />
    <ModalRenderer />
  </AlertProvider>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { AlertProvider } from '@cnd_fe/alert'
import LoadingOverlay from '@/components/feedback/LoadingOverlay.vue'
import ModalRenderer from '@/components/common/ModalRenderer.vue'
import { BlankLayout, DefaultLayout, MainLayout } from "@/layout"
import { usePopup } from '@/composables/usePopup.js'
import { useApiMenu } from '@/composables/useApiMenu.js'
import { PopupManager } from "@/components/overlay/index.js";
import AlertBridgeSetup from '@/components/common/AlertBridgeSetup.vue'

const route = useRoute()

const { popups, fetchPopups } = usePopup()
const { navMenusRaw } = useApiMenu()

/**
 * 현재 라우트와 일치하는 메뉴의 menuId 찾기
 */
const findCurrentMenuId = () => {
  if (!navMenusRaw.value.length) return null
  const collectMatches = (menus, currentPath) => {
    const matches = []
    for (const menu of menus) {
      const link = menu.path || menu.menuLink
      if (link && currentPath.includes(link)) {
        matches.push({ menuId: menu.menuId, linkLen: link.length })
      }
      if (menu.children && menu.children.length) {
        matches.push(...collectMatches(menu.children, currentPath))
      }
    }
    return matches
  }
  const matches = collectMatches(navMenusRaw.value, route.path)
  if (matches.length === 0) return null
  matches.sort((a, b) => b.linkLen - a.linkLen)
  return matches[0].menuId
}

/**
 * 현재 상태에 맞는 팝업 조회
 * - menuId가 쿼리에 명시된 메뉴 페이지에서만 팝업 조회
 * - menuId 없는 페이지(게시판 상세 등)에서는 팝업 표시하지 않음
 */
const loadPopups = async () => {
  const menuId = route.query?.menuId || findCurrentMenuId()
  if (menuId) {
    await fetchPopups({ menuId })
  } else if (route.path === '/') {
    await fetchPopups({ domainId: import.meta.env.VITE_DOMAIN_ID })
  } else {
    popups.value = []
  }
}

// 라우트 변경 시 팝업 조회 — 메뉴 로드 완료 상태에서만 동작
watch(() => route.path, async () => {
  if (navMenusRaw.value.length > 0) {
    await loadPopups()
  }
  // 메뉴 미로드 상태 → 스킵 (레이아웃에서 메뉴 로드 후 watch로 조회)
}, { immediate: true })

// 메뉴 로드 완료 후 menuId 기반 팝업 조회 (레이아웃에서 메뉴 로드 시 발동)
watch(() => navMenusRaw.value, async (menus) => {
  if (menus && menus.length > 0) {
    await loadPopups()
  }
}, { once: true })

// layout 값 읽기 (없으면 'blank'으로 간주)
const layoutKey = computed(() => {
  // query로 강제 지정된 레이아웃이 있으면 최우선
  if (route.query.__layout === 'blank') return 'blank'
  if (route.query.__layout === 'default') return 'default'
  if (route.query.__layout === 'main') return 'main'

  // 그다음 route.meta.layout
  if (route.meta?.layout) return route.meta.layout

  // 최종 fallback
  return 'blank'
})

// 키 → 컴포넌트 매핑
const layoutMap = {
  default: DefaultLayout,
  blank: BlankLayout,
  main: MainLayout,
}

// 실제로 쓸 레이아웃 컴포넌트
const layoutComp = computed(() => layoutMap[layoutKey.value] || BlankLayout)
</script>

